// Confirmation page. Markup mirrors 09-confirmation.html: body.confirm-page
// + .confirm-hero (peach + mint gradients, big check, headline + lede),
// .pnr-card, .confirm-actions, then .conf-content grid with .itin-card
// (segments + passenger lines) on the left and the payment .summary-card
// on the right.

import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { lookupAirport } from "../lib/airports";
import { fmtCabin, fmtPaxType } from "../lib/labels";
import { AirlineLogo } from "../components/AirlineLogo";
import { KairaMark } from "../components/KairaMark";
import type { BookingListItem, Itinerary } from "../lib/types";

export function Confirmation() {
  const { bookingId } = useParams();

  // Add the confirm-page body class for the gradient backdrop in the hero.
  useEffect(() => {
    document.body.classList.add("confirm-page");
    return () => document.body.classList.remove("confirm-page");
  }, []);

  // We need both: the universal itinerary (segments) AND the lightweight
  // BookingListItem (which carries passengers + wallet_debited_inr).
  const itinQ = useQuery({
    queryKey: ["booking", bookingId],
    queryFn: () => api.get<Itinerary>(`/v1/flights/booking/${bookingId}/`),
    enabled: !!bookingId,
  });
  const metaQ = useQuery({
    queryKey: ["bookingMeta", bookingId],
    queryFn: () => api.get<BookingListItem>(`/v1/bookings/${bookingId}/`),
    enabled: !!bookingId,
  });

  if (itinQ.isLoading || metaQ.isLoading) return <div className="app-loading"><span className="spinner" />Loading…</div>;
  if (itinQ.error || !itinQ.data || metaQ.error || !metaQ.data) {
    return <div className="app-error">Could not load booking.</div>;
  }
  const b = itinQ.data;
  const meta = metaQ.data;
  const passengers = meta.passengers || [];
  const lead = passengers[0] || {};
  const legs = b.itinerary_items[0]?.legs || [];
  const lastLeg = legs[legs.length - 1];
  const lastSeg = lastLeg?.segments[lastLeg.segments.length - 1];
  const destCity = lastSeg ? lookupAirport(lastSeg.destination.airport_code).city : "your destination";
  const firstSeg = legs[0]?.segments[0];
  const issuedAt = new Date(meta.modified_at);

  return (
    <>
      <section className="confirm-hero">
        <div className="container">
          {/* Kaira mark sits above the headline as the brand cosign on a
              successful booking. Green check stays beside it as the universal
              "done" signal. */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginBottom: 18 }}>
            <KairaMark size={64} />
            <div className="confirm-checkmark" style={{ margin: 0 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3"><path d="M5 13l4 4L19 7" /></svg>
            </div>
          </div>
          <h1>You're <span className="serif">flying</span> to {destCity}.</h1>
          {firstSeg && (
            <p className="lede">
              {fmtDateLong(firstSeg.origin.departure_time)} — {firstSeg.airline.code} {firstSeg.airline.flight_number} leaves{" "}
              {lookupAirport(firstSeg.origin.airport_code).city}
              {firstSeg.origin.terminal && ` T${firstSeg.origin.terminal}`} at {fmtTime(firstSeg.origin.departure_time)}.
              {lead.email ? <> Confirmation sent to <b>{String(lead.email)}</b>.</> : null}
            </p>
          )}

          <div className="pnr-card">
            <div className="col pnr">
              <div className="lbl">Airline PNR</div>
              <div className="val">
                {b.pnr || <span style={{ color: "var(--ink-4)", fontSize: 14 }}>Pending</span>}
              </div>
              {b.pnr && <CopyChip value={b.pnr} />}
            </div>
            <div className="col">
              <div className="lbl">Provider ref</div>
              <div className="val" style={{ fontSize: 18, fontWeight: 700 }}>
                {b.bms_booking_code || <span style={{ color: "var(--ink-4)", fontSize: 14, fontWeight: 500 }}>—</span>}
              </div>
              {b.bms_booking_code && <CopyChip value={b.bms_booking_code} />}
            </div>
            <div className="col">
              <div className="lbl">Booking ID</div>
              <div className="val" style={{ fontSize: 13, fontFamily: "ui-monospace, SFMono-Regular, monospace", wordBreak: "break-all", lineHeight: 1.4 }}>
                {b.booking_id}
              </div>
              <CopyChip value={b.booking_id} />
            </div>
            <div className="col">
              <div className="lbl">Issued</div>
              <div className="val">{fmtIssuedAt(issuedAt)}</div>
            </div>
          </div>

          <div className="confirm-actions">
            <button type="button" className="btn btn-primary btn-pill">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
              Download e-ticket
            </button>
            <button type="button" className="btn btn-ghost btn-pill">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6" /></svg>
              Email again
            </button>
            <Link to={`/bookings/${bookingId}`} className="btn btn-ghost btn-pill">
              Manage booking
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
            </Link>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="conf-content">
          <div>
            <div className="itin-card">
              <h3>Your <span className="serif">itinerary</span></h3>
              <div className="itin-card-sub">
                {summaryRoute(b)} · {paxLabel(b.pax_count)} · {b.itinerary_items[0]?.is_refundable ? "refundable" : "non-refundable"} {fmtCabin(b.itinerary_items[0]?.legs[0]?.segments[0]?.cabin_class)}
              </div>

              {b.itinerary_items[0]?.legs.map((leg, li) => (
                <div className="segment" key={li}>
                  <div className="seg-dir">{capitalize(leg.direction)} · {fmtDateLong(leg.segments[0]?.origin.departure_time)}</div>
                  {leg.segments.map((s, si) => (
                    <div key={si}>
                      <div className="segment-row">
                        <div className="when"><b>{fmtTime(s.origin.departure_time)}</b><span>{fmtShortDate(s.origin.departure_time)}</span></div>
                        <div className="at"><b>{lookupAirport(s.origin.airport_code).city} · {lookupAirport(s.origin.airport_code).name}</b><span>{s.origin.terminal ? `Terminal ${s.origin.terminal}` : ""}</span></div>
                      </div>
                      <div className="segment-flight">
                        <AirlineLogo code={s.airline.code} name={s.airline.name} />
                        <span><b>{s.airline.code} {s.airline.flight_number} · {s.airline.name}</b> · {fmtCabin(s.cabin_class)} · {fmtDuration(s.duration_minutes)} · {leg.stop_count === 0 ? "Direct" : `${leg.stop_count} stop`}</span>
                      </div>
                      <div className="segment-row">
                        <div className="when"><b>{fmtTime(s.destination.arrival_time)}</b><span>{fmtShortDate(s.destination.arrival_time)}</span></div>
                        <div className="at"><b>{lookupAirport(s.destination.airport_code).city} · {lookupAirport(s.destination.airport_code).name}</b><span>{s.destination.terminal ? `Terminal ${s.destination.terminal}` : ""}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="itin-card">
              <h3>On the <span className="serif">ticket</span></h3>
              <div className="itin-card-sub">Names locked once issued — please verify against passport / ID.</div>
              {passengers.length === 0 ? (
                <div style={{ color: "var(--ink-3)", fontSize: 13 }}>(No passenger data persisted.)</div>
              ) : passengers.map((p, i) => {
                const name = [p.title, p.first_name, p.last_name].filter(Boolean).map(String).join(" ");
                const meta = [
                  fmtPaxType(String(p.pax_type ?? "adult")),
                  p.nationality ? String(p.nationality) : null,
                  p.passport_number ? `Passport ${String(p.passport_number)}` : null,
                ].filter(Boolean).join(" · ");
                return (
                  <div className="passenger-line" key={i}>
                    <div><b>{name}</b><span>{meta}</span></div>
                  </div>
                );
              })}
            </div>
          </div>

          <aside>
            <div className="summary-card">
              <h3>Payment</h3>
              <div className="sub">Wallet debit · {fmtIssuedAt(issuedAt)}</div>
              <div className="fare-line"><span className="lbl">Method</span><span>Partner wallet</span></div>
              <div className="fare-line"><span className="lbl">Booking ID</span><span style={{ fontSize: 12, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>{b.booking_id}</span></div>
              <div className="fare-line"><span className="lbl">Status</span><span className="status-pill confirmed"><span className="dot" />Captured</span></div>
              <div style={{ height: 1, background: "var(--line)", margin: "14px 0" }} />
              <div className="fare-line"><span className="lbl">Base fare</span><span>{fmtMoney(b.pricing.base_fare, b.pricing.currency)}</span></div>
              {b.pricing.tax_and_surcharge != null && (
                <div className="fare-line"><span className="lbl">Taxes &amp; surcharge</span><span>{fmtMoney(b.pricing.tax_and_surcharge, b.pricing.currency)}</span></div>
              )}
              <div className="fare-line total"><span className="lbl">Paid</span><span>{fmtMoney(b.pricing.final_fare, b.pricing.currency)}</span></div>
              {meta.wallet_debited_inr && (
                <div className="fare-line" style={{ color: "var(--ink-3)", fontSize: 12 }}>
                  <span className="lbl">Wallet debit (INR)</span><span>{fmtMoney(parseFloat(meta.wallet_debited_inr), "INR")}</span>
                </div>
              )}

              <p className="field-help" style={{ marginTop: 14 }}>
                Need to change something? Cancellations and date changes are available from{" "}
                <b style={{ color: "var(--ink-2)" }}>Manage booking</b>.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}

function CopyChip({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    if (!value) return;
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    }).catch(() => {});
  }
  return (
    <button type="button" className="copy-chip" onClick={copy} aria-label={`Copy ${value}`}>
      {copied ? (
        <>
          <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
          Copied
        </>
      ) : (
        <>
          <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15V5a2 2 0 0 1 2-2h10" />
          </svg>
          Copy
        </>
      )}
    </button>
  );
}

// helpers
function fmtTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso); return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function fmtDateLong(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso); return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}
function fmtShortDate(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso); return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}
function fmtIssuedAt(d: Date): string {
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) + " · " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function fmtDuration(mins: number): string {
  if (!Number.isFinite(mins)) return "—";
  const h = Math.floor(mins / 60); const m = mins % 60;
  return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${String(m).padStart(2, "0")}m`;
}
function fmtMoney(n: number, currency: string): string {
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(n); }
  catch { return `${currency} ${Math.round(n).toLocaleString("en-IN")}`; }
}
function paxLabel(p?: { adult: number; child: number; infant: number }): string {
  if (!p) return "1 adult";
  const total = p.adult + p.child + p.infant;
  return `${total} ${total === 1 ? "passenger" : "passengers"}`;
}
function summaryRoute(b: Itinerary): string {
  const item = b.itinerary_items[0];
  if (!item) return "";
  const a = item.legs[0].segments[0].origin.airport_code;
  const z = item.legs[0].segments[item.legs[0].segments.length - 1].destination.airport_code;
  const lastLeg = item.legs[item.legs.length - 1];
  const finalDest = lastLeg.segments[lastLeg.segments.length - 1].destination.airport_code;
  if (item.legs.length > 1 && finalDest === a) {
    return `${lookupAirport(a).city} ⇄ ${lookupAirport(z).city}`;
  }
  return `${lookupAirport(a).city} → ${lookupAirport(z).city}`;
}
function capitalize(s: string): string { return s ? s[0].toUpperCase() + s.slice(1) : s; }
